
import React from 'react';
import { NotificationPreferences } from '@/contexts/auth/types';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Tag, ChevronDown } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Profession } from '@/lib/professionTypes';

interface ProfessionPreferencesSectionProps {
  preferences: NotificationPreferences;
  onChange: (preferences: NotificationPreferences) => void;
  professions: Profession[];
}

const ProfessionPreferencesSection: React.FC<ProfessionPreferencesSectionProps> = ({ 
  preferences, 
  onChange,
  professions
}) => {
  const handleTopicChange = (professionId: string, checked: boolean) => {
    const updatedTopics = checked
      ? [...preferences.preferredTopics, professionId]
      : preferences.preferredTopics.filter(id => id !== professionId);

    onChange({ ...preferences, preferredTopics: updatedTopics });
  };

  const professionsByCategory = professions.reduce<Record<string, Profession[]>>((acc, profession) => {
    if (!acc[profession.category]) {
      acc[profession.category] = [];
    }
    acc[profession.category].push(profession);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Tag className="h-4 w-4 text-muted-foreground" />
        <Label className="font-medium">Preferált szakterületek</Label>
      </div>

      <div className="space-y-4">
        {Object.entries(professionsByCategory).map(([category, profs]) => (
          <Collapsible key={category}>
            <CollapsibleTrigger className="flex items-center justify-between w-full p-2 bg-gray-50 rounded-md hover:bg-gray-100">
              <span className="font-medium capitalize">{category}</span>
              <ChevronDown className="h-4 w-4" />
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 ml-4 space-y-2">
              {profs.map((profession) => (
                <div key={profession.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`topic-${profession.id}`}
                    checked={preferences.preferredTopics.includes(profession.id)}
                    onCheckedChange={(checked) =>
                      handleTopicChange(profession.id, checked === true)}
                  />
                  <Label
                    htmlFor={`topic-${profession.id}`}
                    className="text-sm"
                  >
                    {profession.name}
                  </Label>
                </div>
              ))}
            </CollapsibleContent>
          </Collapsible>
        ))}
      </div>
    </div>
  );
};

export default ProfessionPreferencesSection;
